<template>
  <div class="incorporations-list-container">
    <div v-if="showSyncDeliveryStatusButton" class="sync-button-container">
      <CustomButton
        id="sync-delivery-status"
        text="Sincronizar estados de envío"
        variant="transparent"
        :loading="isSyncingStatus"
        :disabled="isSyncingStatus"
        @click="handleSyncDeliveryStatus"
      />
      <InfoButton :messages="[syncDeliveryStatusDescription]" type="info" />
    </div>
    <div v-if="showRegisterButton" class="register-button-container">
      <CustomButton
        id="register-employee"
        text="Registrar nuevo colaborador"
        variant="secondary"
        :show-icon="true"
        :icon-src="addUserIcon"
        icon-alt="Registrar nuevo colaborador"
        :disabled="isLoadingNewEmployee"
        @click="handleRegisterEmployee"
      />
    </div>

    <div class="employees-table-container">
      <div class="table-scroll">
        <table class="data-table">
          <thead class="table-header">
            <tr>
              <th>APELLIDO PATERNO</th>
              <th>APELLIDO MATERNO</th>
              <th>NOMBRE(S)</th>
              <th>ESTADO INCORPORACIÓN</th>
              <th v-if="showRestoreLinkButton">COPIAR ENLACE DE REGISTRO</th>
              <th v-if="showRestoreLinkButton">ENVIAR POR WHATSAPP</th>
              <th>ENVIAR RECORDATORIO</th>
              <th>ENVIADO POR ULTIMA VEZ</th>
              <th>ESTADO DE ENVÍO</th>
              <th>VER HISTORIAL DE ENVÍOS</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="isLoadingEmployees">
              <td :colspan="8" class="loading-row">
                <div class="loading-text">Cargando colaboradores...</div>
              </td>
            </tr>
            <tr v-else-if="filteredTableData.length === 0">
              <td :colspan="8" class="empty-state">
                <div class="empty-state-text">
                  No existen colaboradores en proceso de incorporación
                </div>
              </td>
            </tr>
            <tr
              v-else
              v-for="(item, index) in filteredTableData"
              :key="item.id"
              :class="{ 'row-alt': index % 2 === 1 }"
              class="table-row"
            >
              <td>{{ item.paternalLastName }}</td>
              <td>{{ item.maternalLastName }}</td>
              <td>{{ item.names }}</td>
              <td :class="statusClass(item.incorporationStatus ?? '')">
                {{ item.incorporationStatus }}
              </td>
              <td v-if="showRestoreLinkButton" class="center-cell">
                <button
                  v-if="item.incorporationStatus === IncorporationStatus.NO_INICIADO"
                  class="copy-url-btn"
                  @click="handleCopyRestoreLink(item)"
                  :disabled="loadingRestoreLinkEmployeeId === item.id"
                >
                  <img
                    v-if="loadingRestoreLinkEmployeeId !== item.id"
                    src="@/assets/copy-black.svg"
                    alt="Copiar enlace de registro"
                    width="20"
                    height="20"
                  />
                  <img
                    v-else
                    src="@/assets/mini-loading.gif"
                    alt="Cargando..."
                    width="20"
                    height="20"
                  />
                </button>
              </td>
              <td v-if="showRestoreLinkButton">
                <button
                  v-if="item.incorporationStatus === IncorporationStatus.NO_INICIADO"
                  id="send-whatsapp"
                  @click="openWhatsappTab(item)"
                  class="send-whatsapp-btn"
                  :disabled="loadingWhatsappEmployeeId === item.id"
                >
                  <img
                    v-if="loadingWhatsappEmployeeId !== item.id"
                    src="@/assets/whatsapp-black.svg"
                    alt="Enviar por WhatsApp"
                    width="40"
                    height="40"
                  />
                  <img
                    v-else
                    src="@/assets/mini-loading.gif"
                    alt="Cargando..."
                    width="25"
                    height="25"
                  />
                </button>
              </td>
              <td>
                <button
                  id="send-reminder"
                  @click="sendReminder(item)"
                  :disabled="
                    !canSendReminderUtils(item.deliveryStatus, item.lastDateRemainderEmailSent) ||
                    loadingEmployeeId === item.id
                  "
                  v-if="shouldShowReminderButton(item)"
                  class="send-url-btn"
                >
                  <img
                    :src="
                      getReminderIcon(item.deliveryStatus, item.lastDateRemainderEmailSent, item.id)
                    "
                    alt="Enviar Recordatorio"
                    width="25"
                    height="25"
                  />
                </button>
              </td>
              <td>
                {{
                  item.lastDateRemainderEmailSent
                    ? new Date(item.lastDateRemainderEmailSent).toLocaleDateString('es-MX')
                    : '-'
                }}
              </td>
              <td
                :class="getDeliveryStatusClass(getDisplayDeliveryStatus(item))"
                class="center-cell"
              >
                <div class="delivery-status-container">
                  {{ getDisplayDeliveryStatus(item) }}
                  <InfoButton
                    v-if="getDisplayDeliveryStatus(item).length > 0"
                    :messages="[getDeliveryStatusMessage(item)]"
                    :type="getDisplayDeliveryStatus(item)"
                  />
                </div>
              </td>
              <td class="center-cell">
                <button
                  @click="viewDeliveryHistory(item)"
                  :disabled="item.deliveryStatus === DeliveryStatus.ENVIANDO"
                  class="eye-button"
                >
                  <img
                    src="@/assets/eye-gray-icon.svg"
                    alt="Ver historial de envíos"
                    class="eye-icon"
                  />
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Email History Modal -->
    <EmailHistoryModal
      v-if="showEmailHistoryModal"
      :is-visible="showEmailHistoryModal"
      :employee-name="selectedEmployeeName"
      :email-history="emailHistory"
      :hide-sent="hideSent"
      @close="closeEmailHistoryModal"
      @toggle-hide-sent="toggleHideSent"
    />

    <NewEmployeeModal
      :is-visible="showNewEmployeeModal"
      :is-loading="isLoadingNewEmployee"
      @close="closeNewEmployeeModal"
      @submit="handleNewEmployeeSubmit"
    />

    <ToastMessage
      v-model="showToast"
      :message="toastMessage"
      :submessage="toastSubmessage"
      :duration="10000"
      :type="toastType"
      :icon="toastIcon"
    />
  </div>
</template>

<script setup lang="ts">
import {
  IncorporationStatus,
  EmployeeDTO,
  DeliveryStatus,
  type GroupResponseDTO,
  EmailHistoryDTO,
  CustomerDTO,
  MEMBER_COLUMNS_POSITION,
  RegisterStatus,
  PreRegisterDataResultDto,
} from 'commons'
import { ref, inject, onMounted, computed } from 'vue'
import { ApiService } from '../../services/apiService'
import sendReminderIcon from '../assets/send-reminder.svg'
import sentReminderIcon from '../assets/sent-reminder.svg'
import loadingIcon from '@/assets/mini-loading.gif'
import ToastMessage from '../../components/ToastMessage.vue'
import EmailHistoryModal from './EmailHistoryModal.vue'
import { type ApiError } from '../../types/errors'
import checkIcon from '@/assets/check-icon.svg'
import errorIcon from '@/assets/error-icon-white.svg'
import { deliveryStatusClassFromStatus } from '../../utils/delivery-status-utils'
import { canSendReminderUtils } from '../../utils/sending-email-utils'
import InfoButton from '../../components/InfoButton.vue'
import { DELIVERY_STATUS_INFO } from '../../utils/constants'
import addUserIcon from '@/assets/add-user.svg'
import CustomButton from '../../components/CustomButton.vue'
import NewEmployeeModal from '../../components/NewEmployeeModal.vue'
import { WebEnvironmentConfigService } from '../../services/env-config.service'
import { trackGroupEvent } from '@/utils/googleAnalyticsEventsSender'

const props = defineProps<{
  groupId: string
  showRegisterButton?: boolean
  showRestoreLinkButton?: boolean
}>()

const tableData = ref<EmployeeDTO[]>([])
const apiService = inject<ApiService>('apiService')
const company = ref<GroupResponseDTO | null>(null)

const STALE_DELIVERY_THRESHOLD_MS = 10 * 60 * 1000

// Filter out employees with APROBACION status
const filteredTableData = computed(() => {
  return tableData.value.filter((employee) => {
    const status = employee.incorporationStatus?.trim()
    return (
      status !== IncorporationStatus.APROBACION &&
      status !== 'APROBACIÓN' &&
      status !== 'APROBACION'
    )
  })
})

// Show sync button only when there are messages in ENVIANDO for more than 10 minutes
const showSyncDeliveryStatusButton = computed(() => {
  const threshold = Date.now() - STALE_DELIVERY_THRESHOLD_MS
  return filteredTableData.value.some((item) => {
    const displayStatus = getDisplayDeliveryStatus(item)
    if (displayStatus !== DeliveryStatus.ENVIANDO) return false
    const lastSent = item.lastDateRemainderEmailSent
    if (!lastSent) return false
    return new Date(lastSent).getTime() < threshold
  })
})
const showToast = ref(false)
const toastMessage = ref('')
const toastSubmessage = ref('')
const toastType = ref<'success' | 'error'>('success')
const toastIcon = ref<string | undefined>(undefined)

const showEmailHistoryModal = ref(false)
const selectedEmployeeName = ref('')
const emailHistory = ref<EmailHistoryDTO[]>([])
const hideSent = ref(false)

const loadingEmployeeId = ref<string | null>(null)
const loadingRestoreLinkEmployeeId = ref<string | null>(null)
const loadingWhatsappEmployeeId = ref<string | null>(null)
const showNewEmployeeModal = ref(false)
const isLoadingNewEmployee = ref(false)
const isLoadingEmployees = ref(false)
const isSyncingStatus = ref(false)

const syncDeliveryStatusDescription =
  'Actualiza los estados de envío de los correos que llevan más de 10 minutos en "Enviando".'

const envConfig = new WebEnvironmentConfigService()

onMounted(() => {
  fetchEmployees()
})

if (!apiService) {
  throw new Error('ApiService is not injected')
}

const fetchEmployees = async () => {
  try {
    isLoadingEmployees.value = true
    tableData.value = await apiService.getIncorporationEmployeesByGroup(props.groupId)
  } catch (error) {
    console.error('Hubo un error: Error fetching employees ', error)
  } finally {
    isLoadingEmployees.value = false
  }
}

const fetchCompany = async () => {
  try {
    company.value = await apiService.getGroup(props.groupId)
  } catch (err) {
    console.error('Hubo un error: Error fetching company ', err)
  }
}

const statusClass = (status: string) => {
  switch (status) {
    case IncorporationStatus.NO_INICIADO:
      return 'status-no-started'
    case IncorporationStatus.EN_PROCESO:
      return 'status-pending'
    case IncorporationStatus.RECHAZADO:
      return 'status-rejected'
    default:
      return ''
  }
}

const sendReminder = async (employee: EmployeeDTO) => {
  loadingEmployeeId.value = employee.id!
  try {
    await apiService.postReminderEmail(employee.id!)
    await fetchEmployees()
    showToast.value = true
    toastMessage.value = 'Correo enviado'
    toastSubmessage.value = 'Se ha enviado el correo correctamente.'
    toastType.value = 'success'
    toastIcon.value = checkIcon
  } catch (error) {
    const apiError = error as ApiError
    const errorData = await apiError.response.json()
    toastMessage.value = 'Error'
    toastSubmessage.value = errorData.message
    toastType.value = 'error'
    toastIcon.value = errorIcon
    showToast.value = true
  } finally {
    loadingEmployeeId.value = null
  }
}

const getReminderIcon = (deliveryStatus?: string, lastSent?: Date, employeeId?: string) => {
  if (loadingEmployeeId.value === employeeId) {
    return loadingIcon
  }
  if (canSendReminderUtils(deliveryStatus, lastSent)) {
    return sendReminderIcon
  }
  return sentReminderIcon
}

const handleCopyRestoreLink = async (employee: EmployeeDTO) => {
  if (!employee.email) {
    toastMessage.value = 'Error'
    toastSubmessage.value = 'El colaborador no tiene email registrado'
    toastType.value = 'error'
    toastIcon.value = errorIcon
    showToast.value = true
    return
  }

  loadingRestoreLinkEmployeeId.value = employee.id!
  try {
    const restoreLink = await apiService.getRestorePasswordLink(employee.email)

    // Copy to clipboard
    await navigator.clipboard.writeText(restoreLink)

    toastMessage.value = 'Enlace copiado'
    toastSubmessage.value = 'El enlace ha sido copiado al portapapeles'
    toastType.value = 'success'
    toastIcon.value = checkIcon
    showToast.value = true
  } catch (error) {
    console.error('Hubo un error: Error getting restore password link ', error)
    const apiError = error as ApiError
    let errorMessage = 'No se pudo generar el enlace'

    try {
      const errorData = await apiError.response.json()
      errorMessage = errorData.message || errorMessage
    } catch {
      // If we can't parse the error, use the default message
    }

    toastMessage.value = 'Error'
    toastSubmessage.value = errorMessage
    toastType.value = 'error'
    toastIcon.value = errorIcon
    showToast.value = true
  } finally {
    loadingRestoreLinkEmployeeId.value = null
  }
}

const openWhatsappTab = async (employee: EmployeeDTO) => {
  if (!employee.phone) {
    toastMessage.value = 'Error'
    toastSubmessage.value = 'El colaborador no tiene teléfono registrado'
    toastType.value = 'error'
    toastIcon.value = errorIcon
    showToast.value = true
    return
  }

  if (!employee.email) {
    toastMessage.value = 'Error'
    toastSubmessage.value = 'El colaborador no tiene email registrado'
    toastType.value = 'error'
    toastIcon.value = errorIcon
    showToast.value = true
    return
  }

  loadingWhatsappEmployeeId.value = employee.id!
  try {
    // Generate restore password link
    const restoreLink = await apiService.getRestorePasswordLink(employee.email)

    // Format phone number: remove any spaces, dashes, or parentheses, then add +52 prefix
    const cleanPhone = employee.phone.replace(/\s|-|\(|\)/g, '')
    const phoneWithCountryCode = `+52${cleanPhone}`

    // Encode the message for WhatsApp URL
    const message = encodeURIComponent(restoreLink)

    // Open WhatsApp in a new tab
    const whatsappUrl = `https://wa.me/${phoneWithCountryCode}?text=${message}`
    window.open(whatsappUrl, '_blank')
  } catch (error) {
    console.error('Hubo un error: Error opening WhatsApp ', error)
    const apiError = error as ApiError
    let errorMessage = 'No se pudo generar el enlace'

    try {
      const errorData = await apiError.response.json()
      errorMessage = errorData.message || errorMessage
    } catch {
      // If we can't parse the error, use the default message
    }

    toastMessage.value = 'Error'
    toastSubmessage.value = errorMessage
    toastType.value = 'error'
    toastIcon.value = errorIcon
    showToast.value = true
  } finally {
    loadingWhatsappEmployeeId.value = null
  }
}

const viewDeliveryHistory = async (employee: EmployeeDTO) => {
  try {
    selectedEmployeeName.value = `${employee.paternalLastName} ${employee.maternalLastName} ${employee.names}`
    emailHistory.value = await apiService.getEmailHistoryByEmployeeId(employee.id!)
    showEmailHistoryModal.value = true
  } catch (error) {
    console.error('Hubo un error: Error fetching email history ', error)
    toastMessage.value = 'Error'
    toastSubmessage.value = 'No se pudo cargar el historial de emails'
    toastType.value = 'error'
    toastIcon.value = errorIcon
    showToast.value = true
  }
}

const closeEmailHistoryModal = () => {
  showEmailHistoryModal.value = false
  selectedEmployeeName.value = ''
  emailHistory.value = []
  hideSent.value = false
}

const toggleHideSent = () => {
  hideSent.value = !hideSent.value
}

const getDeliveryStatusClass = (status: string) => {
  return deliveryStatusClassFromStatus(status as DeliveryStatus)
}

const getDisplayDeliveryStatus = (item: EmployeeDTO) => {
  if (item.lastDateRemainderEmailSent && (!item.deliveryStatus || item.deliveryStatus === '')) {
    return DeliveryStatus.ENVIANDO
  }

  return item.deliveryStatus || ''
}

const getDeliveryStatusMessage = (item: EmployeeDTO) => {
  const status = getDisplayDeliveryStatus(item)
  return DELIVERY_STATUS_INFO[status as keyof typeof DELIVERY_STATUS_INFO] || status
}

const shouldShowReminderButton = (item: EmployeeDTO) => {
  return (
    (item.incorporationStatus === IncorporationStatus.EN_PROCESO ||
      item.incorporationStatus === IncorporationStatus.NO_INICIADO) &&
    item.deliveryStatus !== DeliveryStatus.SPAM &&
    item.deliveryStatus !== DeliveryStatus.SUPRIMIDO &&
    item.deliveryStatus !== DeliveryStatus.INEXISTENTE
  )
}

const handleSyncDeliveryStatus = async () => {
  isSyncingStatus.value = true
  try {
    const { updatedCount } = await apiService.syncDeliveryStatus(props.groupId)
    await fetchEmployees()
    showToast.value = true
    toastMessage.value = 'Sincronización completada'
    toastSubmessage.value =
      updatedCount > 0
        ? `Se actualizaron ${updatedCount} estado(s) de envío.`
        : 'No se encontraron estados pendientes de sincronizar.'
    toastType.value = 'success'
    toastIcon.value = checkIcon
  } catch (error) {
    console.error('Hubo un error: Error syncing delivery status ', error)
    showToast.value = true
    toastMessage.value = 'Error'
    toastSubmessage.value = 'No se pudieron sincronizar los estados de envío.'
    toastType.value = 'error'
    toastIcon.value = errorIcon
  } finally {
    isSyncingStatus.value = false
  }
}

const handleRegisterEmployee = () => {
  showNewEmployeeModal.value = true
}

const closeNewEmployeeModal = () => {
  showNewEmployeeModal.value = false
}

const transformFormDataToCustomer = (formData: {
  names: string
  paternalLastName: string
  maternalLastName: string
  email: string
  phone: string
  rfc: string
  membershipType: string
  paymentFrequency: string
}): CustomerDTO => {
  return {
    email: formData.email,
    names: formData.names.toUpperCase(),
    paternalLastName: formData.paternalLastName.toUpperCase(),
    maternalLastName: formData.maternalLastName.toUpperCase(),
    phone: formData.phone,
    productName: formData.membershipType,
    paymentFrequency: formData.paymentFrequency,
    taxId: formData.rfc.toUpperCase(),
    status: RegisterStatus.NOT_REGISTERED_EDITED,
    id: '',
    sendEmail: false,
  }
}

const getApiParams = async () => {
  await fetchCompany()
  return {
    group: company.value?.nombrePublico || '',
    paymentFrequency: company.value?.paymentFrequency || '',
    groupId: props.groupId,
    agentKey: company.value?.agentKey?.trim() || (await envConfig.getDefaultGeneralAgentKey()),
    groupDescription: company.value?.descripcion || '',
    sendNewRegisterEmail: 'true',
  }
}

const showSuccessToast = () => {
  showToast.value = true
  toastMessage.value = 'Colaborador registrado'
  toastSubmessage.value = 'El colaborador ha sido registrado correctamente.'
  toastType.value = 'success'
  toastIcon.value = checkIcon
}

const showErrorToast = (message: string) => {
  showToast.value = true
  toastMessage.value = 'Error'
  toastSubmessage.value = message
  toastType.value = 'error'
  toastIcon.value = errorIcon
}

const handleApiError = async (error: unknown): Promise<string> => {
  const apiError = error as ApiError
  let errorMessage = 'Error al registrar colaborador'
  try {
    const errorData = await apiError.response.json()
    errorMessage = errorData.message || errorMessage
  } catch {
    console.error('Hubo un error: Error fetching error message ', error)
  }
  return errorMessage
}

const handleNewEmployeeSubmit = async (employeeData: unknown) => {
  trackGroupEvent('register_new_member', {
    location: 'Dashboard',
    company_name: company.value?.descripcion,
  })
  isLoadingNewEmployee.value = true

  try {
    const formData = employeeData as {
      names: string
      paternalLastName: string
      maternalLastName: string
      email: string
      phone: string
      rfc: string
      membershipType: string
      paymentFrequency: string
    }

    const customer = transformFormDataToCustomer(formData)
    const apiParams = await getApiParams()

    const batchResponse: PreRegisterDataResultDto = await apiService.preRegisterCustomers(
      '/groups-public/pre-register-single-customer',
      [customer],
      apiParams,
    )

    const isSuccess =
      batchResponse?.membersData?.[0]?.columns?.[MEMBER_COLUMNS_POSITION.STATUS]?.columnValue ===
      RegisterStatus.REGISTERED

    if (isSuccess) {
      showSuccessToast()
      closeNewEmployeeModal()
    } else {
      const errorDescription =
        batchResponse?.membersData?.[0]?.columns?.[MEMBER_COLUMNS_POSITION.STATUS]
          ?.columnDescription || 'El colaborador no ha sido registrado correctamente.'
      showErrorToast(errorDescription)
    }

    await fetchEmployees()
  } catch (error) {
    const errorMessage = await handleApiError(error)
    showErrorToast(errorMessage)
  } finally {
    isLoadingNewEmployee.value = false
  }
}
</script>

<style scoped>
.incorporations-list-container {
  width: 100%;
}

.sync-button-container {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  margin-bottom: 20px;
}

.register-button-container {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 20px;
}

.employees-table-container {
  width: 100%;
  margin: 0 auto;
  overflow-x: auto;
  padding: 0;
  display: flex;
  justify-content: flex-start;
  margin-top: 40px;
}
.data-table {
  border: none;
  border-collapse: collapse;
  position: relative;
}
.data-table thead tr {
  background-color: #003896;
  color: #fff;
}
.data-table tr {
  background-color: #fff;
  border: none;
  position: relative;
}
.data-table tr.row-alt {
  background-color: #f4f7fa;
}
.data-table th,
.data-table td {
  border: none;
}
.data-table tbody tr:hover {
  background-color: #e0e7ef;
  cursor: pointer;
}

.table-row {
  position: relative;
}
.status-no-started {
  color: #b47e08;
  font-weight: bold;
}
.status-completed {
  color: #4eab1f;
  font-weight: bold;
}
.status-rejected {
  color: #d0342c;
  font-weight: bold;
}
.status-pending {
  color: #003896;
  font-weight: bold;
}

.status-unknown {
  color: #6c757d;
  font-weight: normal;
  font-style: italic;
}

.copy-url-btn {
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  transition: background-color 0.2s ease;
  align-items: center;
  justify-content: center;
  display: flex;
  margin: 0 auto;
}

.copy-url-btn:hover {
  background-color: #f0f0f0;
}

.copy-url-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.copy-url-btn:disabled:hover {
  background-color: transparent;
}

.send-whatsapp-btn {
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  transition: background-color 0.2s ease;
  align-items: center;
  justify-content: center;
  display: flex;
  margin: 0 auto;
}

.send-whatsapp-btn:hover {
  background-color: #f0f0f0;
}

.send-whatsapp-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.send-whatsapp-btn:disabled:hover {
  background-color: transparent;
}

.send-url-btn {
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  transition: background-color 0.2s ease;
  align-items: center;
  justify-content: center;
}

.send-url-btn:hover {
  background-color: #f0f0f0;
}

.send-url-btn.copied {
  background-color: #e8f5e8;
}

.center-cell {
  text-align: center;
  vertical-align: middle;
}

.eye-button {
  position: relative;
  width: 24px;
  height: 24px;
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  transition: background-color 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto;
}

.eye-button:hover {
  background-color: #f0f0f0;
}

.eye-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.eye-button:disabled:hover {
  background-color: transparent;
}

.eye-icon {
  width: 20px;
  height: 16px;
  filter: brightness(0) saturate(100%) invert(31%) sepia(0%) saturate(0%) hue-rotate(0deg)
    brightness(95%) contrast(89%);
}

.delivery-status-container {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.delivery-status-container {
  font-weight: 700 !important;
}

.empty-state-message {
  text-align: center;
  font-size: 18px;
  color: #666;
  margin-top: 40px;
  padding: 40px 20px;
  background: #f9f9f9;
  border-radius: 8px;
  border: 1px solid #e0e0e0;
  position: relative;
  z-index: 1;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}
</style>
